// ---------------------------------------------------------------------------
// Digital Notice Board — DesignCanvas
// ---------------------------------------------------------------------------
// The editable "screen". Renders the current slide at its true aspect ratio,
// scaling the logical design resolution down to whatever width the studio panel
// gives it. Owns all direct-manipulation: pointer drag to move, corner handle to
// resize, click to select, click empty space to deselect, and accepting drops
// from the element library. It never mutates state itself — every change is
// bubbled up via onChange so the page keeps a single source of truth (+ undo).
// ---------------------------------------------------------------------------
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Box} from "@chakra-ui/react";
import ElementVisual from "./ElementVisual";
import {clamp} from "../utils/noticeBoardUtils";

export default function DesignCanvas({
    slide,
    canvas,
    selectedId,
    onSelect,
    onChange,
    onDropElement,
}) {
    const wrapRef = useRef(null);
    const [scale, setScale] = useState(0.2);

    // Keep the logical→screen scale in sync with the rendered width.
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const measure = () => setScale(el.clientWidth / canvas.width);
        measure();
        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(measure);
            ro.observe(el);
        }
        window.addEventListener("resize", measure);
        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [canvas.width]);

    const height = canvas.height * scale;

    const startDrag = useCallback(
        (e, el, mode) => {
            e.preventDefault();
            e.stopPropagation();
            onSelect(el.id);
            const startX = e.clientX;
            const startY = e.clientY;
            const orig = {x: el.x, y: el.y, w: el.w, h: el.h};
            const sc = scale;

            const move = (ev) => {
                const dx = (ev.clientX - startX) / sc;
                const dy = (ev.clientY - startY) / sc;
                if (mode === "move") {
                    onChange(el.id, {
                        x: clamp(Math.round(orig.x + dx), 0, canvas.width - orig.w),
                        y: clamp(Math.round(orig.y + dy), 0, canvas.height - orig.h),
                    });
                } else {
                    onChange(el.id, {
                        w: clamp(Math.round(orig.w + dx), 40, canvas.width - orig.x),
                        h: clamp(Math.round(orig.h + dy), 30, canvas.height - orig.y),
                    });
                }
            };
            const up = () => {
                window.removeEventListener("pointermove", move);
                window.removeEventListener("pointerup", up);
            };
            window.addEventListener("pointermove", move);
            window.addEventListener("pointerup", up);
        },
        [scale, canvas.width, canvas.height, onChange, onSelect]
    );

    const handleDrop = (e) => {
        e.preventDefault();
        const type = e.dataTransfer.getData("application/nb-type");
        if (!type || !wrapRef.current) return;
        const rect = wrapRef.current.getBoundingClientRect();
        const lx = (e.clientX - rect.left) / scale;
        const ly = (e.clientY - rect.top) / scale;
        onDropElement(type, lx, ly);
    };

    return (
        <Box
            ref={wrapRef}
            position="relative"
            w="100%"
            h={`${height}px`}
            bg={slide.background}
            borderRadius="6px"
            overflow="hidden"
            boxShadow="0 20px 60px rgba(0,0,0,0.25)"
            onPointerDown={() => onSelect(null)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            style={{
                backgroundImage:
                    "linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)," +
                    "linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)",
                backgroundSize: `${Math.max(1, scale * (canvas.width / 24))}px ${Math.max(
                    1,
                    scale * (canvas.width / 24)
                )}px`,
                touchAction: "none",
            }}
        >
            {slide.elements.map((el) => {
                const selected = el.id === selectedId;
                return (
                    <Box
                        key={el.id}
                        position="absolute"
                        left={`${el.x * scale}px`}
                        top={`${el.y * scale}px`}
                        w={`${el.w * scale}px`}
                        h={`${el.h * scale}px`}
                        transform={el.rotation ? `rotate(${el.rotation}deg)` : undefined}
                        outline={selected ? "2px solid #3182CE" : "1px solid transparent"}
                        _hover={{outline: selected ? "2px solid #3182CE" : "1px solid rgba(49,130,206,0.5)"}}
                        cursor="move"
                        onPointerDown={(e) => startDrag(e, el, "move")}
                        zIndex={selected ? 5 : 1}
                    >
                        <ElementVisual element={el} scale={scale} />

                        {/* Resize handle — bottom-right, only for the selected element */}
                        {selected && (
                            <Box
                                position="absolute"
                                right="-6px"
                                bottom="-6px"
                                w="14px"
                                h="14px"
                                bg="white"
                                border="2px solid #3182CE"
                                borderRadius="3px"
                                cursor="nwse-resize"
                                zIndex={6}
                                onPointerDown={(e) => startDrag(e, el, "resize")}
                            />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
